// UP6: the badge — a numbered favicon in the browser, the app badge through
// the desktop bridge when the Desk runs in Electron. Counts one per session
// with an unseen background notification; clears to zero when the Desk is
// focused. Opt-in: the coordinator never calls this unless the person turned
// the badge on, and a cleared badge restores the original favicon exactly.
export const NOTIFICATION_BADGE_CHANNEL = "desktop:set-notification-badge";

export interface NotificationBadgeBridge {
  readonly setNotificationBadge?: (badge: { readonly count: number }) => Promise<void>;
}

const FAVICON_SELECTOR = 'link[rel~="icon"]';
let originalFavicons: Array<{ readonly link: HTMLLinkElement; readonly href: string }> | null =
  null;

export function badgeLabel(count: number): string {
  return count > 9 ? "9+" : String(count);
}

/** A 64×64 PNG data URL: a red disc with the count. Null when canvas is unavailable (tests, SSR). */
export function renderBadgeImage(
  count: number,
  doc: Document = document,
  baseIcon: HTMLImageElement | null = null,
): string | null {
  if (count <= 0) return null;
  const canvas = doc.createElement("canvas");
  canvas.width = 64;
  canvas.height = 64;
  const ctx = canvas.getContext("2d");
  if (ctx === null) return null;
  if (baseIcon !== null) {
    try {
      ctx.drawImage(baseIcon, 0, 0, 64, 64);
    } catch {
      // A cross-origin or unloaded icon leaves the disc alone.
    }
  }
  ctx.beginPath();
  ctx.arc(40, 24, 22, 0, Math.PI * 2);
  ctx.fillStyle = "#e5484d";
  ctx.fill();
  ctx.fillStyle = "#ffffff";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  const label = badgeLabel(count);
  ctx.font = `600 ${label.length > 1 ? 24 : 30}px system-ui, sans-serif`;
  ctx.fillText(label, 40, 25);
  try {
    return canvas.toDataURL("image/png");
  } catch {
    return null;
  }
}

function faviconLinks(doc: Document): Array<HTMLLinkElement> {
  return Array.from(doc.querySelectorAll<HTMLLinkElement>(FAVICON_SELECTOR));
}

/** Swap the favicon for a numbered one, or restore the original when count is 0. */
export function applyFaviconBadge(count: number, doc: Document = document): void {
  const links = faviconLinks(doc);
  if (count <= 0) {
    if (originalFavicons !== null) {
      for (const { link, href } of originalFavicons) link.href = href;
      originalFavicons = null;
    }
    return;
  }
  const image = renderBadgeImage(count, doc);
  if (image === null) return;
  if (originalFavicons === null) {
    originalFavicons = links.map((link) => ({ link, href: link.href }));
  }
  if (links.length === 0) {
    const link = doc.createElement("link");
    link.rel = "icon";
    doc.head.appendChild(link);
    originalFavicons.push({ link, href: "" });
    links.push(link);
  }
  for (const link of links) link.href = image;
}

/**
 * Set the badge everywhere this Desk can show one. On the desktop the bridge
 * owns the dock or taskbar badge (the main process clears it on focus too);
 * in a browser the favicon carries the count.
 */
export async function setSessionNotificationBadge(
  count: number,
  bridge: NotificationBadgeBridge | undefined = (
    globalThis as { window?: { desktopBridge?: NotificationBadgeBridge } }
  ).window?.desktopBridge,
  doc: Document | undefined = typeof document === "undefined" ? undefined : document,
): Promise<void> {
  if (bridge?.setNotificationBadge !== undefined) {
    try {
      await bridge.setNotificationBadge({ count: Math.max(0, Math.floor(count)) });
    } catch {
      // The desktop refused; the person still sees the in-app notice.
    }
    return;
  }
  if (doc !== undefined) applyFaviconBadge(count, doc);
}

/** Test seam. */
export function resetFaviconBadgeForTests(): void {
  originalFavicons = null;
}
